import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ApiFetch } from '../utils/fetch'

interface ExchangeRates {
  NGN: number
  USD: number
}

interface CurrencyStore {
  currency: 'NGN' | 'USD'
  rates: ExchangeRates | null
  lastFetched: number | null
  setCurrency: (currency: 'NGN' | 'USD') => Promise<void>
  fetchRates: () => Promise<void>
  convertPrice: (price: number, fromCurrency?: 'NGN' | 'USD') => number
  shouldFetchRates: () => boolean
}

const ONE_DAY = 24 * 60 * 60 * 1000 // 24 hours in milliseconds

export const useCurrencyStore = create<CurrencyStore>()(
  persist(
    (set, get) => ({
      currency: 'NGN',
      rates: null,
      lastFetched: null,

      shouldFetchRates: () => {
        const { lastFetched } = get()
        if (!lastFetched) return true
        return Date.now() - lastFetched > ONE_DAY
      },

      fetchRates: async () => {
        try {
          const response = await ApiFetch.get('/currency-converter/fetch-rates')
          
          const rates: ExchangeRates = {
            USD: parseFloat(response.rates.USD) || 1,
            NGN: parseFloat(response.rates.NGN) || 1600
          }
          
          set({ 
            rates,
            lastFetched: Date.now()
          })
        } catch (error) {
          console.error('Failed to fetch exchange rates:', error)
          // Only set fallback if no rates exist
          const { rates } = get()
          if (!rates) {
            set({ 
              rates: {
                USD: 1,
                NGN: 1600
              },
              lastFetched: Date.now()
            })
          }
        }
      },

      setCurrency: async (currency: 'NGN' | 'USD') => {
        const { shouldFetchRates, fetchRates } = get()
        
        // Fetch rates if needed
        if (shouldFetchRates()) {
          await fetchRates()
        }
        
        set({ currency })
      },

      convertPrice: (price: number, fromCurrency: 'NGN' | 'USD' = 'NGN') => {
        const { currency, rates } = get()
        
        if (!rates) {
          return fromCurrency === 'NGN' ? price / 100 : price
        }

        // Convert kobo to naira if source is NGN
        const sourcePrice = fromCurrency === 'NGN' ? price / 100 : price
        
        if (currency === fromCurrency) {
          return sourcePrice
        }

        // Convert from source currency to target currency
        if (fromCurrency === 'NGN' && currency === 'USD') {
          return sourcePrice / rates.NGN
        } else if (fromCurrency === 'USD' && currency === 'NGN') {
          return sourcePrice * rates.NGN
        }
        
        return sourcePrice
      }
    }),
    {
      name: 'currency-store',
      partialize: (state) => ({ 
        currency: state.currency,
        rates: state.rates,
        lastFetched: state.lastFetched
      })
    }
  )
)